import { FormEvent, useEffect, useMemo, useState } from "react";
import { Check, GitBranch, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminStoreOrderStatusItem,
  AdminStoreOrderStatusPayload,
  AdminStoreOrderStatusTransition,
  AdminStoreOrderStatusTransitionPayload,
  AdminStoreSalesOrderOption,
  AdminUser,
  createAdminStoreOrderStatus,
  createAdminStoreOrderStatusTransition,
  deleteAdminStoreOrderStatusTransition,
  getCurrentAdmin,
  listAdminStoreOrderStatuses,
  logout,
  updateAdminStoreOrderStatus,
  updateAdminStoreOrderStatusTransition,
} from "./api";

const emptyTransition: AdminStoreOrderStatusTransitionPayload = {
  FromStatusKey: "",
  ToStatusKey: "",
  FulfillmentType: "Headquarters",
  AllowedActor: "Admin",
  RequiresPaymentSettled: false,
  RequiresArrivedAtStore: false,
  SortOrder: 100,
  IsActive: true,
};

const emptyStatus = {
  StatusKey: "",
  Label: "",
  SortOrder: 100,
  Tone: "muted",
  VisibleInAdmin: true,
  VisibleInStore: true,
  IsTerminal: false,
  IsActive: true,
  Description: "",
};

type ApiState = {
  Statuses: AdminStoreOrderStatusItem[];
  Transitions: AdminStoreOrderStatusTransition[];
  FulfillmentTypeOptions: AdminStoreSalesOrderOption[];
  ActorOptions: AdminStoreSalesOrderOption[];
  ToneOptions: AdminStoreSalesOrderOption[];
};

export default function AdminStoreOrderStatuses() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [statuses, setStatuses] = useState<AdminStoreOrderStatusItem[]>([]);
  const [transitions, setTransitions] = useState<AdminStoreOrderStatusTransition[]>([]);
  const [fulfillmentOptions, setFulfillmentOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [actorOptions, setActorOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [toneOptions, setToneOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [statusEdits, setStatusEdits] = useState<Record<string, AdminStoreOrderStatusPayload>>({});
  const [transitionEdits, setTransitionEdits] = useState<Record<string, AdminStoreOrderStatusTransitionPayload>>({});
  const [newStatus, setNewStatus] = useState(emptyStatus);
  const [newTransition, setNewTransition] = useState<AdminStoreOrderStatusTransitionPayload>(emptyTransition);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState("");

  const statusOptions = useMemo(() => statuses.map((item) => ({ Key: item.StatusKey, Label: item.Label })), [statuses]);

  function applyState(data: ApiState) {
    setStatuses(data.Statuses || []);
    setTransitions(data.Transitions || []);
    setFulfillmentOptions(data.FulfillmentTypeOptions || []);
    setActorOptions(data.ActorOptions || []);
    setToneOptions(data.ToneOptions || []);
    setStatusEdits({});
    setTransitionEdits({});
    setNewTransition((current) => ({
      ...current,
      FromStatusKey: current.FromStatusKey || data.Statuses?.[0]?.StatusKey || "",
      ToStatusKey: current.ToStatusKey || data.Statuses?.[1]?.StatusKey || data.Statuses?.[0]?.StatusKey || "",
    }));
  }

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      applyState(await listAdminStoreOrderStatuses());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "載入失敗，請稍後再試。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function statusValue(status: AdminStoreOrderStatusItem): AdminStoreOrderStatusPayload {
    return statusEdits[status.StatusKey] || status;
  }

  function transitionValue(transition: AdminStoreOrderStatusTransition): AdminStoreOrderStatusTransitionPayload {
    return transitionEdits[transition.StoreSalesOrderStatusTransitionId] || {
      FromStatusKey: transition.FromStatusKey,
      ToStatusKey: transition.ToStatusKey,
      FulfillmentType: transition.FulfillmentType,
      AllowedActor: transition.AllowedActor,
      RequiresPaymentSettled: transition.RequiresPaymentSettled,
      RequiresArrivedAtStore: transition.RequiresArrivedAtStore,
      SortOrder: transition.SortOrder,
      IsActive: transition.IsActive,
    };
  }

  async function saveStatus(status: AdminStoreOrderStatusItem) {
    setSavingKey(`status-${status.StatusKey}`);
    setMessage("");
    try {
      applyState(await updateAdminStoreOrderStatus(status.StatusKey, statusValue(status)));
      setMessage("狀態設定已更新。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "狀態更新失敗。");
    } finally {
      setSavingKey("");
    }
  }

  async function createStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("new-status");
    setMessage("");
    try {
      applyState(await createAdminStoreOrderStatus(newStatus));
      setNewStatus(emptyStatus);
      setMessage("狀態選項已新增，請接著設定流程轉換。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "狀態新增失敗。");
    } finally {
      setSavingKey("");
    }
  }

  async function saveTransition(transition: AdminStoreOrderStatusTransition) {
    setSavingKey(`transition-${transition.StoreSalesOrderStatusTransitionId}`);
    setMessage("");
    try {
      applyState(await updateAdminStoreOrderStatusTransition(transition.StoreSalesOrderStatusTransitionId, transitionValue(transition)));
      setMessage("流程轉換已更新。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "流程轉換更新失敗。");
    } finally {
      setSavingKey("");
    }
  }

  async function removeTransition(transition: AdminStoreOrderStatusTransition) {
    if (!window.confirm(`確定刪除「${transition.FromLabel} → ${transition.ToLabel}」這條流程轉換？`)) return;
    setSavingKey(`delete-${transition.StoreSalesOrderStatusTransitionId}`);
    setMessage("");
    try {
      applyState(await deleteAdminStoreOrderStatusTransition(transition.StoreSalesOrderStatusTransitionId));
      setMessage("流程轉換已刪除。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "流程轉換刪除失敗。");
    } finally {
      setSavingKey("");
    }
  }

  async function createTransition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("new-transition");
    setMessage("");
    try {
      applyState(await createAdminStoreOrderStatusTransition(newTransition));
      setNewTransition((current) => ({ ...emptyTransition, FromStatusKey: current.FromStatusKey, ToStatusKey: current.ToStatusKey }));
      setMessage("流程轉換已新增。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "流程轉換新增失敗。");
    } finally {
      setSavingKey("");
    }
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市訂單狀態設定" user={currentUser} onLogout={handleLogout}>
      <div className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-lg font-bold text-[#203d32]"><GitBranch size={20} />流程狀態維護</div>
            <p className="mt-1 text-sm font-medium leading-6 text-[#69766f]">總管理端與門市端的流程下拉選單會共同讀取這裡的設定；流程轉換會影響訂單可前往的下一步。</p>
          </div>
          <button className="inline-flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2] sm:w-auto" onClick={load} type="button">
            <RefreshCw size={17} />重新整理
          </button>
        </div>
        {message && <div className="mt-4 rounded-md border border-[#cfe3d5] bg-[#f2f9f4] px-4 py-3 text-sm font-bold text-[#245438]">{message}</div>}
      </div>

      <section className="mb-5 rounded-md border border-[#dfe6e1] bg-white">
        <div className="border-b border-[#edf0ee] px-4 py-3">
          <h2 className="font-bold text-[#203d32]">狀態選項</h2>
          <p className="mt-1 text-sm text-[#69766f]">可新增流程狀態並調整名稱、顯示端、排序與啟用狀態；已建立的狀態不提供刪除，避免舊訂單斷鏈。</p>
        </div>
        <form className="border-b border-[#edf0ee] p-4" onSubmit={createStatus}>
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(180px,1fr)_minmax(220px,1.2fr)_160px_120px]">
            <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]">
              狀態代碼
              <input className="h-10 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如 AwaitingTrial" value={newStatus.StatusKey} onChange={(event) => setNewStatus((current) => ({ ...current, StatusKey: event.target.value }))} />
            </label>
            <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]">
              顯示名稱
              <input className="h-10 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如 等待試戴確認" value={newStatus.Label} onChange={(event) => setNewStatus((current) => ({ ...current, Label: event.target.value }))} />
            </label>
            <Select label="色彩" options={toneOptions} value={newStatus.Tone} onChange={(value) => setNewStatus((current) => ({ ...current, Tone: value }))} />
            <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]">
              排序
              <input className="h-10 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={newStatus.SortOrder} onChange={(event) => setNewStatus((current) => ({ ...current, SortOrder: Number(event.target.value) }))} />
            </label>
          </div>
          <div className="mt-3 grid gap-3 2xl:grid-cols-[1fr_auto] 2xl:items-center">
            <div className="grid gap-2 sm:grid-cols-3">
              <CheckBox label="總管理端顯示" checked={newStatus.VisibleInAdmin} onChange={(checked) => setNewStatus((current) => ({ ...current, VisibleInAdmin: checked }))} padded />
              <CheckBox label="門市端顯示" checked={newStatus.VisibleInStore} onChange={(checked) => setNewStatus((current) => ({ ...current, VisibleInStore: checked }))} padded />
              <CheckBox label="終態" checked={newStatus.IsTerminal} onChange={(checked) => setNewStatus((current) => ({ ...current, IsTerminal: checked }))} padded />
            </div>
            <button className="inline-flex h-10 w-full min-w-[140px] items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60 sm:w-auto" disabled={savingKey === "new-status"} type="submit">
              <Plus size={17} />新增狀態
            </button>
          </div>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1260px] border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f]">
              <tr>
                <th className="px-4 py-3">狀態代碼</th>
                <th className="px-4 py-3">顯示名稱</th>
                <th className="px-4 py-3">色彩</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">顯示 / 終態</th>
                <th className="px-4 py-3">說明</th>
                <th className="min-w-[140px] px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-8 text-center text-[#69766f]" colSpan={7}>載入中...</td></tr>
              ) : statuses.map((status) => {
                const edit = statusValue(status);
                return (
                  <tr className="border-t border-[#edf0ee] align-top" key={status.StatusKey}>
                    <td className="px-4 py-4">
                      <div className="font-bold text-[#203d32]">{status.StatusKey}</div>
                      {status.IsSystem && <span className="mt-2 inline-flex rounded-full bg-[#eef3f8] px-2 py-1 text-xs font-bold text-[#255071]">系統狀態</span>}
                    </td>
                    <td className="px-4 py-4">
                      <input className="h-10 w-full min-w-[160px] rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={edit.Label || ""} onChange={(event) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, Label: event.target.value } }))} />
                    </td>
                    <td className="px-4 py-4">
                      <select className="h-10 w-full min-w-[120px] rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={edit.Tone || "muted"} onChange={(event) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, Tone: event.target.value } }))}>
                        {toneOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-4">
                      <input className="h-10 w-24 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={edit.SortOrder ?? 100} onChange={(event) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, SortOrder: Number(event.target.value) } }))} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="grid gap-2 text-sm font-bold text-[#315746]">
                        <CheckBox label="總管理端" checked={Boolean(edit.VisibleInAdmin)} onChange={(checked) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, VisibleInAdmin: checked } }))} />
                        <CheckBox label="門市端" checked={Boolean(edit.VisibleInStore)} onChange={(checked) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, VisibleInStore: checked } }))} />
                        <CheckBox label="終態" checked={Boolean(edit.IsTerminal)} onChange={(checked) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, IsTerminal: checked } }))} />
                        <CheckBox label="啟用" checked={Boolean(edit.IsActive)} onChange={(checked) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, IsActive: checked } }))} />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <textarea className="min-h-[88px] w-full min-w-[260px] rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={edit.Description || ""} onChange={(event) => setStatusEdits((current) => ({ ...current, [status.StatusKey]: { ...edit, Description: event.target.value } }))} />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button className="inline-flex h-10 min-w-[110px] items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={savingKey === `status-${status.StatusKey}`} onClick={() => saveStatus(status)} type="button">
                        <Save size={16} />儲存
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="border-b border-[#edf0ee] px-4 py-3">
          <h2 className="font-bold text-[#203d32]">流程轉換</h2>
          <p className="mt-1 text-sm text-[#69766f]">控制每一種交付方式在指定狀態下，可以往哪個下一步前進。</p>
        </div>
        <form className="border-b border-[#edf0ee] p-4" onSubmit={createTransition}>
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(170px,1fr)_minmax(170px,1fr)_160px_140px_110px]">
            <Select label="來源狀態" options={statusOptions} value={newTransition.FromStatusKey} onChange={(value) => setNewTransition((current) => ({ ...current, FromStatusKey: value }))} />
            <Select label="下一狀態" options={statusOptions} value={newTransition.ToStatusKey} onChange={(value) => setNewTransition((current) => ({ ...current, ToStatusKey: value }))} />
            <Select label="交付方式" options={fulfillmentOptions} value={newTransition.FulfillmentType} onChange={(value) => setNewTransition((current) => ({ ...current, FulfillmentType: value }))} />
            <Select label="操作端" options={actorOptions} value={newTransition.AllowedActor} onChange={(value) => setNewTransition((current) => ({ ...current, AllowedActor: value }))} />
            <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]">
              排序
              <input className="h-10 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={newTransition.SortOrder} onChange={(event) => setNewTransition((current) => ({ ...current, SortOrder: Number(event.target.value) }))} />
            </label>
          </div>
          <div className="mt-3 grid gap-3 2xl:grid-cols-[1fr_auto] 2xl:items-center">
            <div className="grid gap-2 sm:grid-cols-2">
              <CheckBox label="需結清尾款" checked={newTransition.RequiresPaymentSettled} onChange={(checked) => setNewTransition((current) => ({ ...current, RequiresPaymentSettled: checked }))} padded />
              <CheckBox label="需已到店" checked={newTransition.RequiresArrivedAtStore} onChange={(checked) => setNewTransition((current) => ({ ...current, RequiresArrivedAtStore: checked }))} padded />
            </div>
            <button className="inline-flex h-10 w-full min-w-[120px] items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60 sm:w-auto" disabled={savingKey === "new-transition"} type="submit">
              <Plus size={17} />新增
            </button>
          </div>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1260px] border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f]">
              <tr>
                <th className="px-4 py-3">來源</th>
                <th className="px-4 py-3">下一步</th>
                <th className="px-4 py-3">交付方式</th>
                <th className="px-4 py-3">操作端</th>
                <th className="px-4 py-3">條件</th>
                <th className="px-4 py-3">排序 / 啟用</th>
                <th className="min-w-[220px] px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {transitions.map((transition) => {
                const edit = transitionValue(transition);
                return (
                  <tr className="border-t border-[#edf0ee] align-top" key={transition.StoreSalesOrderStatusTransitionId}>
                    <td className="px-4 py-4"><Select options={statusOptions} value={edit.FromStatusKey} onChange={(value) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, FromStatusKey: value } }))} /></td>
                    <td className="px-4 py-4"><Select options={statusOptions} value={edit.ToStatusKey} onChange={(value) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, ToStatusKey: value } }))} /></td>
                    <td className="px-4 py-4"><Select options={fulfillmentOptions} value={edit.FulfillmentType} onChange={(value) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, FulfillmentType: value } }))} /></td>
                    <td className="px-4 py-4"><Select options={actorOptions} value={edit.AllowedActor} onChange={(value) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, AllowedActor: value } }))} /></td>
                    <td className="px-4 py-4">
                      <div className="grid gap-2 text-sm font-bold text-[#315746]">
                        <CheckBox label="需結清尾款" checked={edit.RequiresPaymentSettled} onChange={(checked) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, RequiresPaymentSettled: checked } }))} />
                        <CheckBox label="需已到店" checked={edit.RequiresArrivedAtStore} onChange={(checked) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, RequiresArrivedAtStore: checked } }))} />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="grid gap-2">
                        <input className="h-10 w-24 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={edit.SortOrder} onChange={(event) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, SortOrder: Number(event.target.value) } }))} />
                        <CheckBox label="啟用" checked={edit.IsActive} onChange={(checked) => setTransitionEdits((current) => ({ ...current, [transition.StoreSalesOrderStatusTransitionId]: { ...edit, IsActive: checked } }))} />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button className="inline-flex h-10 min-w-[96px] items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={savingKey === `transition-${transition.StoreSalesOrderStatusTransitionId}`} onClick={() => saveTransition(transition)} type="button">
                          <Check size={16} />儲存
                        </button>
                        <button className="inline-flex h-10 min-w-[96px] items-center justify-center gap-2 rounded-md border border-[#f0b6b6] bg-white px-4 font-bold text-[#9a2d2d] hover:bg-[#fff5f5] disabled:opacity-60" disabled={savingKey === `delete-${transition.StoreSalesOrderStatusTransitionId}`} onClick={() => removeTransition(transition)} type="button">
                          <Trash2 size={16} />刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}

function Select({ label, options, value, onChange }: { label?: string; options: AdminStoreSalesOrderOption[]; value: string; onChange: (value: string) => void }) {
  const input = (
    <select className="h-10 w-full min-w-0 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
    </select>
  );
  if (!label) return input;
  return <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]">{label}{input}</label>;
}

function CheckBox({ label, checked, onChange, padded = false }: { label: string; checked: boolean; onChange: (checked: boolean) => void; padded?: boolean }) {
  return (
    <label className={`inline-flex min-h-10 min-w-0 items-center gap-2 rounded-md ${padded ? "border border-[#dfe6e1] bg-[#fbfcfb] px-3" : ""}`}>
      <input checked={checked} className="h-4 w-4 shrink-0 accent-[#315746]" onChange={(event) => onChange(event.target.checked)} type="checkbox" />
      <span className="min-w-0 whitespace-nowrap text-sm font-bold text-[#315746]">{label}</span>
    </label>
  );
}
